// ─── analyticsHelpers.js ────────────────────────────────────────────────────
// Shared colors, formatters, and constants for the analytics dashboard.
// Import from here — never define these locally in tab components.

export const API = process.env.REACT_APP_API_URL;

// ─── Formatters ─────────────────────────────────────────────────────────────

export const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

export const formatNumber = (n) => {
    if (n == null) return '—';
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
};

export const formatPercent = (n) => {
    if (n == null) return '—';
    return `${Math.round(n)}%`;
};

export const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Category colors ─────────────────────────────────────────────────────────

export const CATEGORY_COLORS = {
    food: '#ef4444',        drinks: '#f97316',       bar: '#a855f7',
    coffee: '#92400e',      dessert: '#ec4899',       food_truck: '#f59e0b',
    restaurant: '#10b981',  stage: '#6366f1',         music: '#8b5cf6',
    dj: '#06b6d4',          performance: '#14b8a6',   activity: '#84cc16',
    vendor: '#3b82f6',      retail: '#0ea5e9',        merchandise: '#f43f5e',
    sponsor: '#eab308',     booth: '#64748b',          art: '#d946ef',
    craft: '#fb923c',       market_stall: '#4ade80',  pop_up: '#38bdf8',
    lounge: '#a3e635',      info: '#22d3ee',           restroom: '#94a3b8',
    medical: '#f87171',     security: '#fbbf24',       entrance: '#34d399',
    exit: '#f87171',        parking: '#60a5fa',        kids: '#fb7185',
    family_zone: '#fde68a', vip_lounge: '#c084fc',    photo_spot: '#f472b6',
    charging_station: '#4ade80', networking: '#818cf8', water_station: '#38bdf8',
    lost_and_found: '#fbbf24', chill_zone: '#86efac',
};

export const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || '#64748b';

// ─── Chart palette for non-category data ─────────────────────────────────────

export const CHART_PALETTE = [
    '#2B8EE8', '#10b981', '#f59e0b', '#ec4899',
    '#8b5cf6', '#06b6d4', '#f97316', '#84cc16',
];

// ─── Device + entry source colors ────────────────────────────────────────────

export const DEVICE_COLORS = {
    mobile:  '#2B8EE8',
    desktop: '#10b981',
    tablet:  '#f59e0b',
};

export const SOURCE_COLORS = {
    qr:     '#2B8EE8',
    link:   '#8b5cf6',
    direct: '#94A3B8',
};

// ─── Shared sub-components ───────────────────────────────────────────────────

export const KPICard = ({ label, value, sub, accent, trend }) => (
    <div className="kpi-card" style={{ '--accent': accent }}>
        {trend != null && (
            <div className={`kpi-trend ${trend >= 0 ? 'up' : 'down'}`}>
                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
            </div>
        )}
        <div className="kpi-value">{value}</div>
        <div className="kpi-label">{label}</div>
        {sub && <div className="kpi-sub">{sub}</div>}
    </div>
);

export const SplitBar = ({ data, colors, showCounts = true }) => {
    const total = Object.values(data || {}).reduce((a, b) => a + b, 0);
    if (!data || total === 0) return <div className="split-bar-empty">No data yet</div>;
    return (
        <div className="split-bar-wrap">
            <div className="split-bar">
                {Object.entries(data).map(([key, val]) => (
                    <div
                        key={key}
                        className="split-bar-segment"
                        style={{
                            width: `${(val / total) * 100}%`,
                            background: colors[key] || '#94A3B8',
                        }}
                        title={`${key}: ${val}`}
                    />
                ))}
            </div>
            <div className="split-bar-legend">
                {Object.entries(data).map(([key, val]) => (
                    <span key={key} className="split-legend-item">
                        <span className="split-dot" style={{ background: colors[key] || '#94A3B8' }} />
                        {key}
                        {showCounts && <strong> {val}</strong>}
                        {!showCounts && (
                            <strong> {Math.round((val / total) * 100)}%</strong>
                        )}
                    </span>
                ))}
            </div>
        </div>
    );
};

export const EmptyState = ({ message = 'No data yet', icon }) => (
    <div className="empty-state">
        {icon && <div className="empty-state-icon">{icon}</div>}
        <span>{message}</span>
    </div>
);

export const SectionTitle = ({ children, sub, action }) => (
    <div className="section-title-row">
        <div>
            <h3 className="ad-card-title">{children}</h3>
            {sub && <p className="ad-card-sub">{sub}</p>}
        </div>
        {action && action}
    </div>
);